import { useEffect, useState } from "react";
import { Link, useNavigate, Navigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { getDefaultRoute } from "@/lib/rbac";
import XLSyncLogo from "@/components/brand/XLSyncLogo";

export default function Login() {
  const { signIn, isAuthenticated, isLoading, permissions, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const message = location.state?.toastMessage;
    if (!message) return;
    toast.success(message);
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.pathname, location.state, navigate]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-[var(--xl-bg)]">
        <div className="w-8 h-8 rounded-full border-2 border-[var(--xl-primary)] border-t-transparent animate-spin" />
      </div>
    );
  }
  if (isAuthenticated) {
    return <Navigate to={getDefaultRoute(permissions, user?.role)} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError("Email and password are required");
      return;
    }
    setSubmitting(true);
    setError("");
    const { ok, error: err, permissions: newPerms, user: signedInUser } = await signIn(email, password);
    setSubmitting(false);
    if (ok) {
      toast.success("Welcome back");
      navigate(getDefaultRoute(newPerms ?? [], signedInUser?.role), { replace: true });
    } else {
      setError(err || "Sign-in failed");
    }
  };

  return (
    <div
      data-testid="login-page"
      className="min-h-screen flex items-center justify-center bg-[var(--xl-bg)] px-4"
    >
      <div className="w-full max-w-sm">
        <div className="bg-[var(--xl-surf)] rounded-2xl border border-[var(--xl-line)] shadow-[0_8px_30px_rgba(22,32,46,0.06)] overflow-hidden">
          <div
            className="flex flex-col items-center px-4 pt-4 pb-3 border-b border-[var(--xl-line)] bg-[var(--xl-surf)]"
            data-testid="login-brand"
          >
            <XLSyncLogo size="xl" className="w-full max-w-[280px]" />
            <p className="text-sm text-[var(--xl-mut)] mt-2">
              Sign in to your account
            </p>
          </div>

          <div className="p-6 space-y-5">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs text-[var(--xl-mut)]">
                Email address
              </Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                autoComplete="email"
                placeholder="you@hospital.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="border-[var(--xl-line2)] focus-visible:ring-primary/25"
                required
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs text-[var(--xl-mut)]">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type={showPw ? "text" : "password"}
                  autoComplete="current-password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pr-10 border-[var(--xl-line2)] focus-visible:ring-primary/25"
                  required
                />
                <button
                  type="button"
                  data-testid="login-toggle-password"
                  onClick={() => setShowPw((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--xl-mut2)] hover:text-[var(--xl-text)]"
                  aria-label={showPw ? "Hide password" : "Show password"}
                >
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <div className="flex justify-end">
                <Link
                  to="/forgot-password"
                  data-testid="login-forgot-password-link"
                  className="text-xs text-[var(--xl-primary)] hover:underline"
                >
                  Forgot password?
                </Link>
              </div>
            </div>

            {error && (
              <div
                data-testid="login-error"
                className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              data-testid="login-submit-btn"
              disabled={submitting}
              className="w-full h-11 rounded-full font-semibold"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Signing in…
                </>
              ) : (
                "Sign in"
              )}
            </Button>
          </form>

          <div className="pt-4 border-t border-[var(--xl-line)] text-xs text-[var(--xl-mut)] text-center">
            Need access? Contact your XLSync administrator.
          </div>
          </div>
        </div>

        <p className="text-xs text-[var(--xl-mut2)] text-center mt-6 space-x-2">
          <span>XLSync · Clinical imaging intelligence · Admin-invite only</span>
          <span aria-hidden>·</span>
          <Link
            to="/privacy"
            data-testid="login-privacy-link"
            className="text-[var(--xl-primary)] hover:underline"
          >
            Privacy Policy
          </Link>
        </p>
      </div>
    </div>
  );
}
